import { otherJobsChoice } from '../offer-agreement-choice'
import { addressLine } from '../address-words'
import { currencySymbol, postcodeWords } from '../round-config'
import { openFollowupDialog } from '../followup-dialog'
import { askAgainLater, followupSummary } from '../followup'
import { api, mutate, readCache, withBase } from '../api'
import { theirWords } from '../queue-chip'
import { openBooking } from '../booking'
import { priceCellText } from '../pricing'
import { confirmFieldsKnown, cycleRisk, followUpDue, inWorkLists, needsConfirming, noDateYet, noNextDate } from '../attention'
import { paymentWordsBadge, visitBadge } from '../visit-badge'
import { standingWords } from '../visit-words'
import { detailsReasons, dueDate, dueIsGuessed, matchesSearch, needsDetails, rebooked, upcomingVisits } from '../planning'
import { areaOf, sortCustomers, sortKeyForColumn, type SortDir } from '../sorting'
import { todayISO } from '../ui'
import { routeGen } from '../route-state'
import { customersCsv, owedByCustomer, visitsCsv, type OwedRead, type VisitRow } from '../customer-export'
import { getMe } from '../me'
import { addressOneLine, readWorkColumns, writeWorkColumns } from '../work-columns'
import { listTitle, shelved, visitSummary, recanvass, takenOff, listSummary } from '../customer-context'
import { openVisitDetailsFor } from './rebook'
import { lineDrawing, type Drawing } from '../picture-tiles'
import '../plan-round.css'
import { multilineField, textareaRows } from '../field-lines'
import type { Clean, Customer } from '../types'
import { clear, cycleLabel, cycleNoteIsMeaningful, cycleOptions, cycleValueFor, dayLabel, el, loadError, money, parseMoney, shortDate, toast, roundConfirmation } from '../ui'
import { AREA_RENAMED, areaRenameLink } from '../area-rename'

// The grid — the Work list replacement. Spreadsheet-look: thin borders,
// frozen header, click-to-edit cells. Every edit is a version-guarded
// PATCH through the retry queue; nothing is optimistic.

const customers = new Map<number, Customer>()
let filterText = ''
let statusFilter = 'active'
let viewFilter = 'all'
let areaFilter = ''
// A renamed area keeps its filter under its new name (area-rename.ts).
window.addEventListener(AREA_RENAMED, (e) => { const { from, to } = (e as CustomEvent<{ from: string; to: string }>).detail; if (areaFilter === from) areaFilter = to })
// Default per user preference: the workbook's own row order (id order — see
// sorting.ts 'added'). Column sorting replaces it; the dropdown's first entry
// is the one-select way back.
let sortBy = 'added'
let sortDir: SortDir = 'asc'
let fullSheet = false
let savedScroll = { left: 0, top: 0 }
// 'confirm' / 'unbooked' / 'details' and the two risk views run the SAME pure
// predicates the diary's round summary counts with (attention.ts /
// planning.ts), so a count and the filtered list behind it cannot drift apart.
// 'slipping' / 'going cold' are reachable from that summary's links.
// 'parked' sits next to 'followup' because they are the two halves of one
// field: a follow-up date still to come (parked, quiet everywhere until then)
// and one that has arrived (follow up due). Without it a customer told "not
// now, ask again 5 Oct" was invisible from the moment of the tap until the
// date — out of Messages, out of Due this week, out of the badge, and in no
// list that says so (Liam, 2026-09-18: "it shouldn't take them out of the
// messages, though, should it?"). Being out of Messages is right; being
// nowhere was not.
const VIEWS = [['all', 'All'], ['due', 'Due now'], ['slipping', 'Slipping'], ['cold', 'Going cold'], ['recanvass', 'Recanvass'], ['taken_off', 'Taken off'], ['visit', 'Needs a visit'], ['parked', 'Ask again later'], ['followup', 'Due to ask again'], ['confirm', 'To confirm'], ['nodate', 'No date yet'], ['unbooked', 'No next date'], ['details', 'Missing a price or cycle'], ['nocycle', 'No cycle'], ['noorigin', 'No source']] as const
// The ones shown as buttons; the rest sit under "More", named, and the one
// picked from there shows on the More button (Astra review 2026-09-25).
// Design A (Liam, 23 Oct 2026, "go with recommended"): the six shown are picture tiles with their counts; Recanvass,
// Taken off and the rest are under More lists.
const TILE_DRAWINGS: Record<string, Drawing> = { all: 'people', due: 'time', confirm: 'calendarTick', cold: 'snowflake', visit: 'house', details: 'priceTag' }
const MAIN_VIEWS = new Set(Object.keys(TILE_DRAWINGS))

// One dropdown entry per sortable column key, so whichever column was last
// clicked the select can still SHOW it — a key with no option would silently
// display as the first entry and lie about the order on screen. The default
// keeps its combined meaning (next booked visit, else the cycle-computed due
// date); 'due_cycle' is the Due column's own value, never mixed with
// 'next_booked'.
const SORT_OPTIONS: [string, string][] = [
  ['added', 'Sort: order added'],
  ['due', 'Sort: next / due date'],
  ['due_cycle', 'Sort: due (cycle)'],
  ['next_booked', 'Sort: next visit'],
  ['last_clean', 'Sort: last clean'],
  ['location', 'Sort: area / postcode'],
  ['area', 'Sort: area (sheet)'],
  ['name', 'Sort: name'],
  ['address', 'Sort: address'],
  ['postcode', 'Sort: postcode'],
  ['phone', 'Sort: phone'],
  ['price', 'Sort: price'],
  ['cycle_days', 'Sort: cycle'],
  ['status', 'Sort: status'],
  ['to_call', 'Sort: call?'],
  ['maybe_lapsed', 'Sort: maybe?'],
  ['notes', 'Sort: notes'],
  // The extra columns sort like any other, so each needs its entry here too —
  // a key with no option would make the dropdown show "order added" while the
  // sheet was sorted by something else.
  ['tier', 'Sort: tier'],
  ['origin', 'Sort: where from'],
  ['job_notes', 'Sort: job notes'],
  ['first_price', 'Sort: first clean price'],
  ['inside_price', 'Sort: inside price'],
  ['time_taken', 'Sort: time taken'],
  ['call_notes', 'Sort: call notes'],
  ['payment_increase', 'Sort: payment increase'],
  ['preferred_times', 'Sort: preferred times'],
  ['parking_restrictions', 'Sort: parking'],
]

let editing = false
let refreshPending = false
// Every cell edit fires a fresh read of /api/customers while the previous one
// may still be out. routeGen only catches a change of SCREEN, so without a
// sequence the OLDER answer can land last and repaint the row with the value
// the user just replaced (the same guard schedule.ts and rebook.ts use).
let requestSequence = 0

type FieldType = 'text' | 'money' | 'int' | 'bool' | 'status' | 'cycle'

interface ColDef {
  field: keyof Customer | 'last_clean' | 'next_booked' | 'location' | 'due' | 'actions' | 'visit_details'
  label: string
  type?: FieldType
  width?: string
}

const COLS: ColDef[] = [
  { field: 'name', label: 'Name', type: 'text', width: '16rem' },
  { field: 'area', label: 'Area', type: 'text', width: '9rem' },
  { field: 'address', label: 'Address', type: 'text', width: '16rem' },
  { field: 'postcode', get label() { return postcodeWords('Postcode') }, type: 'text', width: '6.5rem' },
  { field: 'phone', label: 'Phone', type: 'text', width: '8rem' },
  { field: 'cycle_days', label: 'Cycle', type: 'cycle', width: '8rem' },
  { field: 'reg_price_pence', label: 'Regular price', type: 'money', width: '5rem' },
  { field: 'last_clean', label: 'Last clean', width: '6.5rem' },
  { field: 'next_booked', label: 'Next visit', width: '6.5rem' },
  { field: 'status', label: 'Status', type: 'status', width: '5.5rem' },
  { field: 'to_call', label: 'Call?', type: 'bool', width: '3rem' },
  { field: 'maybe_lapsed', label: 'Maybe?', type: 'bool', width: '3rem' },
  { field: 'notes', label: 'Notes', type: 'text', width: '14rem' },
]

// Widths chosen so the whole planning list fits a laptop screen with the
// Booking column (Book / Edit visit, Message) always in view on the right
// (redesign, Liam 2026-09-25: the Book button sat cut off past the edge).
const PLANNING_COLS: ColDef[] = [
  { ...COLS[0], width: '12rem' }, { field: 'location', get label() { return postcodeWords('Area / postcode') }, width: '8.5rem' }, { ...COLS[7], width: '7.5rem' },
  { field: 'due', label: 'Due', width: '8rem' }, { ...COLS[8], width: '9.5rem' }, { ...COLS[5], width: '6.5rem' }, COLS[6],
  { field: 'actions', label: 'Booking', width: '12rem' },
]

// Extra columns (Liam 2026-09-16): customer details outside the current
// standard view, added on demand and sorted like any other column. He asked for Tier
// first — it appeared on no list at all, only on a customer's own page, so
// "who are my tier 1s" meant opening them one at a time. Now remembered per
// business on this device (Liam, 2026-09-30), with none on until asked for.
const EXTRA_COLS: ColDef[] = [
  { field: 'address', label: 'Address', type: 'text', width: '11rem' },
  { field: 'tier', label: 'Tier', type: 'text', width: '4.5rem' },
  { field: 'origin', label: 'Where from', type: 'text', width: '10rem' },
  { field: 'job_notes', label: 'Job notes', type: 'text', width: '14rem' },
  { field: 'first_clean_price_pence', label: 'First clean price', type: 'money', width: '5.5rem' },
  { field: 'inside_price_pence', label: 'Inside price', type: 'money', width: '5.5rem' },
  { field: 'time_taken', label: 'Time taken', type: 'text', width: '7rem' },
  { field: 'call_notes', label: 'Call notes', type: 'text', width: '12rem' },
  { field: 'payment_increase', label: 'Payment increase', type: 'text', width: '8rem' },
  { field: 'preferred_times', label: 'Preferred times', type: 'text', width: '9rem' },
  { field: 'parking_restrictions', label: 'Parking', type: 'text', width: '10rem' },
]
const extraFields = EXTRA_COLS.map(c => c.field)
let extraOn = new Set<string>()
let columnsBusiness: number | null | undefined
function restoreColumns(): void {
  const businessId = getMe()?.businessId ?? null
  if (businessId === columnsBusiness) return
  columnsBusiness = businessId
  extraOn = readWorkColumns(businessId, extraFields)
}
const chosenExtras = (): ColDef[] => EXTRA_COLS.filter(c => !isStandard(c.field) && extraOn.has(c.field))
// An extra already in the view it was asked for must not appear twice: the
// full sheet owns Notes and the planning sheet owns neither, so the guard is
// against the CURRENT base, not a fixed list.
const isStandard = (field: string): boolean => (fullSheet ? COLS : PLANNING_COLS).some(c => c.field === field)

// The Columns fold closes on a tap anywhere else, and on Escape — the same way
// the account menu does (main.ts). A <details> left to itself stays open until
// its own summary is tapped again, and Liam tapped the page behind it expecting
// it to shut (2026-09-16). Bound once on the document, because render() builds
// a fresh picker every time and a per-picker listener would pile up.
let pickerDismissBound = false
function bindPickerDismiss(): void {
  if (pickerDismissBound) return
  pickerDismissBound = true
  const closeOpenPickers = (keep: Node | null): void => {
    for (const box of document.querySelectorAll<HTMLDetailsElement>('details.col-picker[open]')) {
      // A tap INSIDE the fold is ticking a column, not dismissing it.
      if (!keep || !box.contains(keep)) box.open = false
    }
  }
  document.addEventListener('click', (e) => closeOpenPickers(e.target as Node))
  document.addEventListener('keydown', (e) => { if (e.key === 'Escape') closeOpenPickers(null) })
  window.addEventListener('hashchange', () => closeOpenPickers(null))
}

// The Booking column is the row's action, so it stays last whichever extras
// are on: an added column must never sit between the row and the button.
const columns = (): ColDef[] => {
  // Address follows Name so the person and place can be read together.
  // Full sheet already has its own address column in its original position.
  const address = !fullSheet && extraOn.has('address') ? [EXTRA_COLS[0]] : []
  const standard = fullSheet ? COLS : [PLANNING_COLS[0], ...address, ...PLANNING_COLS.slice(1)]
  const originalBase = ['visit','recanvass','taken_off'].includes(viewFilter)
    ? [standard[0], { field: 'visit_details', label: viewFilter === 'visit' ? 'Visit details' : 'List details', width: '18rem' } as ColDef, ...standard.slice(1)]
    : standard
  const base = ['recanvass','taken_off'].includes(viewFilter) ? originalBase.map(col=>col.field==='actions'?{...col,label:'Next action'}:col.field==='due'?{...col,label:'Regular due'}:col.field==='next_booked'?{...col,label:'Next booked visit'}:col) : originalBase
  const extras = chosenExtras().filter(c => c.field !== 'address')
  if (!extras.length) return base
  const hasActions = base[base.length - 1].field === 'actions'
  return hasActions
    ? [...base.slice(0, -1), ...extras, base[base.length - 1]]
    : [...base, ...extras]
}

function displayValue(c: Customer, col: ColDef): string {
  const v = c[col.field as keyof Customer]
  // Regular price with the ad-hoc price beside it: an ad-hoc or missed-cycle
  // visit is charged the dearer one, and the column alone made £55 look wrong.
  if (col.field === 'reg_price_pence') return priceCellText(c, money)
  if (col.type === 'money') return money(v as number | null)
  if (col.type === 'bool') return (v as number) ? '✓' : ''
  if (col.type === 'cycle') return cycleLabel(c.cycle_days, c.cycle_text)
  // Dates read as "15 May", not the raw "2026-05-15" the database stores.
  if (col.field === 'last_clean' || col.field === 'next_booked') return shortDate(v as string | null)
  return v === null || v === undefined ? '' : String(v)
}

function commitField(c: Customer, col: ColDef, raw: string | boolean | null, td: HTMLTableCellElement): void {
  // The cycle dropdown writes BOTH stored fields at once, so the number and the
  // wording can never drift apart again — which is how one customer ended up
  // reading "8 weeks" with no number behind it and no date could be worked out.
  if (col.type === 'cycle') {
    const v = String(raw ?? '')
    const patch: Record<string, unknown> =
      v.startsWith('d')
        ? { cycle_days: parseInt(v.slice(1), 10) }
        : (() => {
            const o = cycleOptions()[parseInt(v, 10)]
            if (!o) return { cycle_days: null, cycle_text: null }
            // A note saying more than the label survives the change.
            const keepNote = cycleNoteIsMeaningful(c.cycle_days, c.cycle_text)
            // A choice with its own words (One off) saves them over a kept note, or it would read "Not set".
            return { cycle_days: o.days, cycle_text: o.marker ?? (keepNote ? c.cycle_text : null) }
          })()
    td.classList.add('cell-pending')
    void mutate({
      label: `Cycle: ${c.name}`,
      method: 'PATCH',
      path: `/api/customers/${c.id}`,
      // With what this row showed (withBase): a cycle is exact-version only on the server, so this only names the boxes.
      body: withBase(c, { version: c.version, ...patch }),
      onAck: (ack) => {
        customers.set(c.id, { ...customers.get(c.id)!, ...ack.row as Customer })
        renderRow(c.id)
      },
      onConflict: () => {
        td.classList.remove('cell-pending')
        td.classList.add('cell-conflict')
        td.title = 'Changed somewhere else first. Tap to reload Work.'
      },
    }).then((ack) => {
      if (!ack.ok && ack.status >= 400 && ack.status < 500 && ack.status !== 409) {
        td.classList.remove('cell-pending')
        td.classList.add('cell-error')
        td.title = ack.error ?? 'rejected'
        window.alert(ack.error ?? 'rejected')
      }
    })
    return
  }
  let value: unknown
  try {
    if (col.type === 'money') value = parseMoney(String(raw ?? ''))
    else if (col.type === 'int') value = raw === null || String(raw).trim() === '' ? null : parseInt(String(raw), 10)
    else if (col.type === 'bool') value = raw ? 1 : 0
    else value = raw === null || String(raw).trim() === '' ? null : String(raw).trim()
    if (col.type === 'int' && value !== null && Number.isNaN(value)) throw new Error('not a number')
  } catch (err) {
    td.classList.add('cell-error')
    td.title = (err as Error).message
    return
  }
  const label = `${col.label}: ${c.name}`
  td.classList.add('cell-pending')
  void mutate({
    label,
    method: 'PATCH',
    path: `/api/customers/${c.id}`,
    // What this cell showed when it was changed (same-time saves): a change to a box nobody else changed saves even
    // when someone else changed this customer since Work was read.
    body: withBase(c, { version: c.version, [col.field]: value }),
    onAck: (ack) => {
      // The server's row is now the freshest thing on this screen, so any read
      // still in flight is stale by definition — retire its sequence or it
      // repaints the cell with the value that was just replaced.
      requestSequence++
      customers.set(c.id, { ...customers.get(c.id)!, ...ack.row as Customer })
      renderRow(c.id)
    },
    onConflict: (ack) => {
      td.classList.remove('cell-pending')
      td.classList.add('cell-conflict')
      // Who changed this box to what, when the server said (Liam, 15 Oct 2026).
      td.title = ack.changed?.length ? `Not saved: ${ack.changed.map(theirWords).join(' and ')} first. Tap to reload Work.` : 'Changed somewhere else first. Tap to reload Work.'
    },
    onQueued: () => {
      // stays visibly pending; the queue chip carries the truth
    },
  }).then((ack) => {
    // codex CRITICAL-2: a terminal 4xx drops the mutation from the queue —
    // without this the cell would sit "pending" forever on a dead edit.
    // A 409 is onConflict's: the cell already says who changed it, and the raw words are never put up over it.
    if (!ack.ok && ack.status >= 400 && ack.status < 500 && ack.status !== 409) {
      td.classList.remove('cell-pending')
      td.classList.add('cell-error')
      td.title = ack.error ?? 'rejected'
      window.alert(ack.error ?? 'rejected')
    }
  })
}

// The clean row sitting behind a derived date column, or null if there isn't one.
function cleanRefFor(c: Customer, field: string): { id: number; version: number; date: string } | null {
  if (field === 'last_clean' && c.last_clean_id && c.last_clean && c.last_clean_version != null) {
    return { id: c.last_clean_id, version: c.last_clean_version, date: c.last_clean }
  }
  if (field === 'next_booked' && c.next_booked_id && c.next_booked && c.next_booked_version != null) {
    return { id: c.next_booked_id, version: c.next_booked_version, date: c.next_booked }
  }
  return null
}

// Editing a derived date edits the CLEAN it came from — a real date picker,
// version-guarded like every other edit, straight from the grid.
function openDateEditor(c: Customer, col: ColDef, td: HTMLTableCellElement): void {
  if (editing) return
  const ref = cleanRefFor(c, col.field as string)
  const isLast = col.field === 'last_clean'
  editing = true
  const finish = (): void => {
    editing = false
    if (refreshPending) {
      refreshPending = false
      void load()
    }
  }
  clear(td)
  td.classList.add('cell-editing')
  const input = el('input', { type: 'date', class: 'cell-input', 'aria-label': col.label, max: todayISO() })
  input.value = ref?.date ?? ''
  let done = false
  const commit = (): void => {
    if (done) return
    done = true
    const next = input.value
    finish()
    renderCell(c, col, td)
    td.focus()
    if (!next || next === ref?.date) return
    if (isLast && next > todayISO()) { toast('A completed clean cannot be in the future'); return }
    td.classList.add('cell-pending')
    // No clean behind the cell yet → create one. "Last clean" makes a done
    // clean at that date (which also books the next visit off their cycle);
    // "Next booked" makes a booking.
    const create = ref === null
    void mutate({
      label: `${create ? 'Add' : 'Move'} ${col.label.toLowerCase()}: ${c.name}`,
      method: create ? 'POST' : 'PATCH',
      path: create ? `/api/customers/${c.id}/cleans` : `/api/cleans/${ref.id}`,
      // restore_if_removed says "this price is a guess off the customer record,
      // not a figure anyone typed" — so a clean removed and put back returns
      // with its own money rather than the guess. Only this screen may say it:
      // the customer screen's log form types real amounts (see reinstate.ts).
      body: create
        ? isLast
          ? {
              date: next,
              amount_pence: c.reg_price_pence,
              state: 'done',
              payment_status: 'unknown',
              restore_if_removed: true,
            }
          : { date: next, amount_pence: c.reg_price_pence, confirmed: 0, restore_if_removed: true }
        : { version: ref.version, date: next },
      onAck: (ack) => {
        // A date typed back onto a clean that was removed reinstates the
        // original row, price and all. Say so: the cell shows a date, and both
        // the money and the payment state it brought back are invisible here.
        if (ack.restored) {
          const row = ack.row as { amount_pence?: number | null; payment_status?: string | null } | undefined
          const at = typeof row?.amount_pence === 'number' ? ` at ${money(row.amount_pence)}` : ''
          const paid = row?.payment_status === 'paid' ? ', still marked paid' : ''
          toast(`Put the ${shortDate(next)} ${isLast ? 'clean' : 'booking'} back${at}${paid}`)
        }
        void load()
      },
      onConflict: () => {
        td.classList.remove('cell-pending')
        td.classList.add('cell-conflict')
        td.title = 'This visit was changed somewhere else first. Tap to reload.'
      },
    }).then((ack) => {
      if (!ack.ok && ack.status >= 400 && ack.status < 500) {
        td.classList.remove('cell-pending')
        td.classList.add('cell-error')
        td.title = ack.error ?? 'rejected'
        window.alert(ack.error ?? 'rejected')
      }
    })
  }
  input.addEventListener('keydown', (e) => {
    if (e.key === 'Enter') commit()
    if (e.key === 'Escape') {
      done = true
      finish()
      renderCell(c, col, td)
      td.focus()
    }
  })
  input.addEventListener('blur', commit)
  td.append(input)

  // Removing a date needs its own control: a date picker has no honest "empty"
  // state, so without this there was no way to take a clean back out — and no
  // way to re-test what happens when you add one.
  if (ref) {
    const del = el('button', { class: 'cell-remove', title: 'remove this date', type: 'button' }, '✕')
    // mousedown, not click: clicking blurs the input first, which re-renders
    // the cell and destroys this button before its click ever lands.
    del.addEventListener('mousedown', (e) => {
      e.preventDefault()
      e.stopPropagation()
      const what = isLast ? 'last clean' : 'booking'
      // Removing a last clean also withdraws the visit the app booked off it,
      // so the confirm has to name that date too. A booking a human chose, or
      // one the customer has CONFIRMED, is never touched and never mentioned —
      // the warning has to mean something the one time it appears.
      const generated = c.next_booked_source === 'suggested' || c.next_booked_source === 'tentative'
      const mayGo = Boolean(isLast && c.next_booked && generated && !c.next_booked_confirmed)
      const alsoGoes = mayGo ? `\n\nThe ${shortDate(c.next_booked!)} visit the app booked from it goes too.` : ''
      if (!window.confirm(`Remove the ${shortDate(ref.date)} ${what} for ${c.name}?${alsoGoes}`)) return
      done = true
      finish()
      renderCell(c, col, td)
      td.classList.add('cell-pending')
      void mutate({
        label: `Remove ${what}: ${c.name}`,
        method: 'DELETE',
        path: `/api/cleans/${ref.id}`,
        body: { version: ref.version },
        onAck: (ack) => {
          // The dialog made a promise about the next visit; the server decides
          // whether it could keep it (the date may not be derivable from this
          // clean, or another editor may have moved it). Correct the record out
          // loud rather than letting the grid quietly disagree with the dialog.
          const removed = ack.removedBookings ?? []
          if (removed.length) toast(`Also removed the ${shortDate(removed[0].date)} visit`)
          else if (mayGo) toast(`The ${shortDate(c.next_booked!)} visit was kept: it is not Roundly's to remove`)
          void load()
        },
        onConflict: () => {
          td.classList.remove('cell-pending')
          td.classList.add('cell-conflict')
          td.title = 'Changed somewhere else first. Tap to reload.'
        },
      }).then((ack) => {
        if (!ack.ok && ack.status >= 400 && ack.status < 500) {
          td.classList.remove('cell-pending')
          window.alert(ack.error ?? 'rejected')
          void load()
        }
      })
    })
    td.append(del)
  }
  input.focus()
}

function openEditor(c: Customer, col: ColDef, td: HTMLTableCellElement): void {
  // Status is where the customer stands: changed in the customer page's Where they stand box, with its Undo and
  // everything it does (a pause resets the messages count, a stop keeps their bookings to review), never a bare
  // field save here (bug hunt 8 Oct 2026).
  if (col.field === 'status') { window.location.hash = `#/customer/${c.id}?stand=1`; return }
  if (editing) return
  editing = true
  const finish = (): void => {
    editing = false
    if (refreshPending) {
      refreshPending = false
      void load()
    }
  }

  if (col.type === 'bool') {
    const next = !(c[col.field as keyof Customer] as number)
    finish()
    commitField(c, col, next, td)
    return
  }

  clear(td)
  td.classList.add('cell-editing')

  if (col.type === 'cycle') {
    const select = el('select', { class: 'cell-input', 'aria-label': col.label })
    const chosen = cycleValueFor(c.cycle_days, c.cycle_text)
    cycleOptions().forEach((o, i) => {
      const opt = el('option', { value: String(i) }, o.label)
      if (chosen === String(i)) opt.selected = true
      select.append(opt)
    })
    // An imported cycle that isn't one of the standard options stays
    // selectable rather than being silently reset to "not set".
    if (c.cycle_days && !cycleOptions().some((o) => o.days === c.cycle_days)) {
      const opt = el('option', { value: `d${c.cycle_days}` }, cycleLabel(c.cycle_days, null))
      opt.selected = true
      select.append(opt)
    }
    // Closing exactly once. `change` re-rendered the cell, which detached the
    // <select>, which fired its own `blur`, which re-rendered AGAIN and threw
    // "removeChild ... no longer a child of this node" — and that exception
    // killed the handler before the save ran, so picking an option silently did
    // nothing. Affected Status too, not just Cycle.
    let closed = false
    const close = (val?: string): void => {
      if (closed) return
      closed = true
      finish()
      renderCell(c, col, td)
      if (val !== undefined) commitField(c, col, val, td)
    }
    select.addEventListener('change', () => close(select.value))
    select.addEventListener('blur', () => close())
    td.append(select)
    select.focus()
    return
  }

  const current = c[col.field as keyof Customer]
  // A note keeps its lines (field-lines.ts); a one-line box glued them together on save.
  const input = col.type !== 'money' && multilineField(String(col.field), current)
    ? el('textarea', { class: 'cell-input cell-textarea', 'aria-label': col.label, rows: String(textareaRows(current)) })
    : el('input', { class: 'cell-input', 'aria-label': col.label, ...(col.type === 'money' ? { inputmode: 'decimal' } : col.field === 'phone' ? { type: 'tel' } : {}) })
  input.value =
    col.type === 'money' && current !== null && current !== undefined
      ? ((current as number) / 100).toFixed(2)
      : current === null || current === undefined
        ? ''
        : String(current)
  const opened = input.value
  let done = false
  const commit = (): void => {
    if (done) return
    done = true
    const raw = input.value
    finish()
    renderCell(c, col, td)
    td.focus()
    // Nothing typed, nothing saved.
    if (raw !== opened) commitField(c, col, raw, td)
  }
  input.addEventListener('keydown', (ev) => {
    const e = ev as KeyboardEvent
    // In a note, Enter starts a new line; Ctrl or Cmd with Enter saves.
    if (e.key === 'Enter' && (input.tagName !== 'TEXTAREA' || e.metaKey || e.ctrlKey)) commit()
    if (e.key === 'Escape') {
      done = true
      finish()
      renderCell(c, col, td)
      td.focus()
    }
  })
  input.addEventListener('blur', commit)
  td.append(input)
  input.focus()
  input.select()
}

// The next visit in the Diary's and Messages' words (visit-words.ts; bug hunt 8 Oct 2026: Work said "confirmed" where
// the Diary said "Day agreed · time to confirm"). The same visit Messages describes (job 1's next visit), with the
// moved-visit facts the list carries for it (upcoming_visits).
function nextVisitBadge(c: Customer): HTMLElement {
  const moved = upcomingVisits(c)?.find((v) => v.id === c.next_booked_id)
  return visitBadge({ date: c.next_booked!, confirmed: c.next_booked_confirmed ?? 0, source: (c.next_booked_source ?? 'manual') as Clean['source'], state: 'booked',
    time_slot: c.next_booked_time_slot?.trim() || null, time_agreed_for: c.next_booked_time_agreed_for ?? null,
    moved_from: moved?.moved_from ?? null, moved_on: moved?.moved_on ?? null, moved_told: moved?.moved_told ?? null, dated_on: moved?.dated_on ?? null }, c)
}
// The latest completed visit's payment, in Owed's words ("Not paid", "Chased · not paid").
function lastPaymentBadge(c: Customer): HTMLElement {
  return paymentWordsBadge({ date: c.last_clean ?? '', confirmed: 0, source: null, state: 'done', payment_status: c.last_payment_status ?? undefined, payment_method: c.last_payment_method, chased: c.last_payment_chased ?? undefined })
}

function visitDetailsButton(c: Customer): HTMLButtonElement {
  const button = el('button', { type: 'button', class: 'btn-secondary', 'aria-label': `Visit details for ${c.name}` }, 'Visit details')
  button.addEventListener('click', e => { e.stopPropagation(); openVisitDetailsFor(c) })
  return button
}
function renderCell(c: Customer, col: ColDef, td: HTMLTableCellElement): void {
  clear(td)
  td.className = ''
  td.title = ''
  td.dataset.field = String(col.field)
  if (col.field === 'visit_details') { td.append(viewFilter === 'visit' ? visitSummary(c) : listSummary(c)); return }
  if (col.field === 'status' && c.customer_list) {td.textContent=listTitle(c);td.title='Open customer to manage this list';return}
  // Where they stand, in the words the customer page and the Diary use (visit-words.ts standingWords).
  if (col.field === 'status') { td.textContent = standingWords(c) || 'On the round'; td.classList.add('cell-editable'); td.title = 'tap to change where they stand'; return }
  if (col.field === 'location') {
    td.append(el('span', {}, c.area ?? ''), el('span', { class: 'cell-secondary' }, c.postcode ?? postcodeWords('Postcode not set')))
    // The postcode half is editable here; area lives in Full sheet.
    td.classList.add('cell-editable')
    td.title = postcodeWords('tap to edit the postcode')
    return
  }
  if (col.field === 'due') {
    const due = dueDate(c)
    td.textContent = due ? shortDate(due) : 'Not set'
    if (due && dueIsGuessed(c)) td.append(el('span', { class: 'cell-secondary' }, 'Guessed · set cycle'))
    // Due is worked out (last clean + cycle), so there is nothing to type
    // into — a tap opens the booking panel on that date to set it by hand.
    td.classList.add('cell-editable')
    td.title = c.next_booked_id ? 'tap to move the next visit' : due && due < todayISO() ? 'tap to book the visit, from today' : 'tap to book the visit on a date you choose'
    // Red means "this one has fallen off the round", which is what Slipping
    // and Going cold count. A customer with an AGREED future visit has not:
    // painting their past due-date red made two thirds of the column shout at
    // work already in the diary. Matches cycleRisk, including its refusal to
    // judge a cached payload that predates next_confirmed.
    if (due && due < todayISO() && c.status === 'active' && c.next_confirmed === null) td.classList.add('due-overdue')
    // Same rule as the diary's review box: a past booking is only work while
    // nothing has replaced it. With a real visit agreed ahead of them the line
    // shouted about work already in the diary, next to a due date this very
    // cell refuses to paint red for the same reason.
    if (c.overdue_bookings && !rebooked(c)) td.append(el('span', { class: 'cell-secondary' }, `${c.overdue_bookings} past booking${c.overdue_bookings === 1 ? '' : 's'} to review`))
    return
  }
  if (col.field === 'actions') {
    const book = el('button', { type: 'button', class: 'btn-primary', 'aria-label': `${c.next_booked_id ? 'Edit visit for' : 'Book visit for'} ${c.name}` }, c.next_booked_id ? 'Edit visit' : 'Book visit')
    book.addEventListener('click', e => { e.stopPropagation(); openBooking({ customerId: c.id, cleanId: c.next_booked_id ?? undefined }) })
    const contact = el('button', { type: 'button', 'aria-label': `Message ${c.name}` }, 'Message')
    contact.addEventListener('click', e => { e.stopPropagation(); openFollowupDialog('customers', c) })
    if (['recanvass','taken_off'].includes(viewFilter)) td.append(el('a',{class:'btn-primary',href:`#/customer/${c.id}`},'Review customer'))
    else td.append(book, viewFilter === 'visit' ? visitDetailsButton(c) : contact)
    const summary = followupSummary(c, todayISO(), 'customer')
    if (summary) td.append(el('span', { class: 'cell-secondary' }, summary))
    return
  }
  const text = displayValue(c, col)
  if (col.field === 'name') {
    const link = el('a', { href: `#/customer/${c.id}`, class: 'name-link' }, text)
    // The name cell is BOTH a link and an editable cell, so opening a customer
    // used to bubble into the cell editor: the editor opened, was torn down by
    // the navigation, and its blur handler committed — firing a PATCH that
    // wrote the name back to itself on every single tap. Harmless online (the
    // API no-ops an identical PATCH), but offline it parked a phantom edit in
    // the retry queue that the user never made. Editing a name is still
    // possible — tap the cell beside the link.
    link.addEventListener('click', (e) => e.stopPropagation())
    td.append(link)
    // The optional Address column takes this line's place, so each planning
    // row shows the address once whichever way the owner chooses.
    if (!fullSheet && !extraOn.has('address')) {
      const address = addressOneLine(c.address)
      const addressLink = el('a', { href: `#/customer/${c.id}`, class: 'cell-secondary work-address', title: address || 'Address not set' }, address || 'Address not set')
      // Like the name link, this opens the customer without starting an
      // editor whose blur would save a name the owner never changed.
      addressLink.addEventListener('click', (e) => e.stopPropagation())
      td.append(addressLink)
    }
    if (c.status === 'lapsed') link.classList.add('name-lapsed')
    else if (c.maybe_lapsed) link.classList.add('name-maybe')
  } else if (col.field === 'address' && !fullSheet) {
    const address = addressOneLine(c.address)
    td.append(el('span', { class: 'work-address', title: address || 'Address not set' }, address || 'Address not set'))
  } else {
    td.textContent = text
  }
  if (col.field === 'next_booked' && c.next_booked) {
    td.append(el('span', { class: 'cell-secondary' }, nextVisitBadge(c)))
  }
  // Latest visit payment — the badge on a Last clean cell describes THAT
  // completed visit only, never the customer's whole account.
  if (col.field === 'last_clean' && c.last_clean) {
    td.append(el('span', { class: 'cell-secondary' }, 'Latest visit: ', lastPaymentBadge(c)))
  }
  // An ACTIVE customer with nothing booked next gets the one cue that matters
  // on the round: work that has no follow-up visit. Lapsed/paused rows stay
  // quiet — their emptiness is already what those states mean.
  if (col.field === 'next_booked' && !c.next_booked && c.status === 'active') {
    td.append(el('span', { class: 'cell-secondary' }, el('span', { class: 'badge badge-unbooked', title: 'No next visit booked. This is not about payment.' }, 'Not rebooked')))
  }
  // Last clean / Next visit live on the CLEAN, not the customer — but they are
  // the booking panel edits next visits; the date cell edits completed work.
  if (col.field === 'last_clean' || col.field === 'next_booked') {
    // Editable whether or not there is a date yet: with one, picking a date
    // moves that clean; with none, picking a date CREATES it. An empty cell
    // that does nothing is the thing that reads as broken.
    td.classList.add('cell-editable')
    td.title = cleanRefFor(c, col.field) ? 'tap to change this date' : 'tap to add one'
  } else {
    td.classList.add('cell-editable')
  }
}

// One listener per td lifetime, reading the CURRENT row from the map at
// click time — renderCell stays a pure content-setter (a listener added
// inside renderCell would accumulate on every re-render).
// The planning list pins its Booking column to the right edge; a cell the
// keyboard moves to must not sit hidden underneath it (Astra review
// 2026-09-25). Scroll just enough to show it beside the pinned column.
function revealBesidePinned(cell: HTMLTableCellElement): void {
  const wrap = document.querySelector<HTMLElement>('#grid-table.planning-sheet')
  if (!wrap) return
  const row = cell.parentElement as HTMLTableRowElement
  const pinned = row.cells[row.cells.length - 1]
  if (!pinned || pinned === cell) return
  const box = wrap.getBoundingClientRect()
  const r = cell.getBoundingClientRect()
  const limit = box.right - pinned.getBoundingClientRect().width
  if (r.right > limit) wrap.scrollLeft += r.right - limit + 8
  else if (r.left < box.left) wrap.scrollLeft -= box.left - r.left + 8
}

function makeCell(id: number, col: ColDef): HTMLTableCellElement {
  const td = el('td', {})
  const c = customers.get(id)!
  renderCell(c, col, td)
  td.tabIndex = -1
  td.addEventListener('focus', () => {
    document.querySelectorAll<HTMLElement>('.sheet td[tabindex="0"]').forEach(cell => cell.tabIndex = -1)
    td.tabIndex = 0
  })
  td.addEventListener('keydown', e => {
    if (e.target !== td) return
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault()
      const action = td.querySelector<HTMLElement>('button, a')
      if (col.field === 'actions' && action) action.click()
      else td.click()
    }
    const row = td.parentElement as HTMLTableRowElement
    const rowIndex = row.sectionRowIndex
    const colIndex = td.cellIndex
    const offsets: Record<string, [number, number]> = { ArrowDown: [1, 0], ArrowUp: [-1, 0], ArrowRight: [0, 1], ArrowLeft: [0, -1] }
    const offset = offsets[e.key]
    if (offset) {
      e.preventDefault()
      const target = (row.parentElement as HTMLTableSectionElement).rows[rowIndex + offset[0]]?.cells[colIndex + offset[1]]
      target?.focus()
      if (target) revealBesidePinned(target)
    }
  })
  if (col.field === 'actions' || col.field === 'visit_details') return td
  if (col.field === 'location') {
    td.addEventListener('click', () => {
      const current = customers.get(id)
      if (current && !td.classList.contains('cell-editing')) openEditor(current, { field: 'postcode', label: postcodeWords('Postcode'), type: 'text', width: '6.5rem' }, td)
    })
    return td
  }
  if (col.field === 'due') {
    td.addEventListener('click', () => {
      const current = customers.get(id)
      if (!current) return
      // A due date that has passed opens on today, as the row's own Book visit does: it booked visits in the past.
      const due = dueDate(current)
      openBooking({ customerId: current.id, cleanId: current.next_booked_id ?? undefined, date: current.next_booked_id || !due || due < todayISO() ? undefined : due })
    })
    return td
  }
  if (col.field === 'last_clean' || col.field === 'next_booked') {
    td.addEventListener('click', () => {
      if (td.classList.contains('cell-conflict')) {
        void load()
        return
      }
      const current = customers.get(id)
      if (current && !td.classList.contains('cell-editing')) {
        if (col.field === 'next_booked') openBooking({ customerId: current.id, cleanId: current.next_booked_id ?? undefined })
        else openDateEditor(current, col, td)
      }
    })
  } else {
    td.addEventListener('click', () => {
      // A conflicted cell is tappable → reload (kimi S-9): a hover-only
      // tooltip is invisible on mobile, which is where conflicts land.
      if (td.classList.contains('cell-conflict')) {
        void load()
        return
      }
      const current = customers.get(id)
      if (current && !td.classList.contains('cell-editing')) openEditor(current, col, td)
    })
  }
  return td
}

function renderRow(id: number): void {
  // An ack landing mid-edit would wipe the editor the user has open
  // (codex HIGH-7) — defer to a full reload when the editor closes.
  if (editing) {
    refreshPending = true
    return
  }
  const table = document.querySelector<HTMLElement>('#grid-table')
  if (!table || !customers.has(id)) return
  const active = document.activeElement as HTMLElement | null
  const field = active?.closest<HTMLElement>('td')?.dataset.field
  const activeId = active?.closest<HTMLElement>('tr')?.dataset.customerId
  renderTable(table)
  renderCount()
  if (field) document.querySelector<HTMLElement>(`tr[data-customer-id="${activeId}"] td[data-field="${field}"]`)?.focus()
}

function viewMatches(c: Customer, view: string): boolean {
  if (view === 'recanvass') return recanvass(c)
  if (view === 'taken_off') return takenOff(c)
  if (view === 'visit') return shelved(c)
  // One rule with the Diary's Needs you (attention.ts inWorkLists): its "Also in Work: N" counts exactly these people.
  if (!inWorkLists(c) && ['due','confirm','nodate','unbooked','slipping','cold','followup','parked'].includes(view)) return false
  if (view === 'due') { const due = dueDate(c); return !!due && due <= todayISO() }
  if (view === 'confirm') return needsConfirming(c)
  if (view === 'nodate') return noDateYet(c)
  if (view === 'unbooked') return noNextDate(c)
  if (view === 'details') return needsDetails(c)
  if (view === 'slipping') return cycleRisk(c) === 'slipping'
  if (view === 'cold') return cycleRisk(c) === 'cold'
  if (view === 'followup') return followUpDue(c)
  // Parked: a follow-up date still ahead of them, however it was set — the
  // diary's "Not now, ask again on…" and the customer's own follow-up dialog
  // write the same field, and both mean the same thing to the round. The
  // Booking cell already says which date on every row.
  if (view === 'parked') return askAgainLater(c)
  // The two Analysis "Things to fix" lists, deep-linked as #/grid?view=nocycle
  // and ?view=noorigin. No cycle counts ACTIVE customers only — a lapsed
  // customer's missing cycle is not a fix, it is why they lapsed.
  if (view === 'nocycle') return c.status === 'active' && !c.cycle_days
  if (view === 'noorigin') { const o = c.origin?.trim(); return !o || o === '?' }
  return true
}

function baseMatches(c: Customer, view = viewFilter, forCount = false): boolean {
  return (forCount && ['recanvass','taken_off'].includes(view) || statusFilter === 'all' || (statusFilter === 'stopped' ? c.status === 'paused' && c.message_reply === 'stopped' : c.status === statusFilter && !(statusFilter === 'paused' && c.message_reply === 'stopped'))) && (!areaFilter || areaOf(c) === areaFilter) && matchesSearch(c, filterText)
}
function matches(c: Customer): boolean { return baseMatches(c) && viewMatches(c, viewFilter) }
function rowsToShow(): Customer[] {
  // sorting.ts owns the rules: copy (never the map's records), blanks last in
  // both directions, dates chronological, prices/cycles numeric, ties on id.
  return sortCustomers([...customers.values()].filter(matches), sortBy, sortDir)
}

// Header click: toggle direction on the active column, otherwise start
// ascending on the new one. The dropdown AND the direction button follow so
// neither shows a stale field after the table was re-sorted from the headers.
function applySort(key: string): void {
  if (sortBy === key) sortDir = sortDir === 'asc' ? 'desc' : 'asc'
  else { sortBy = key; sortDir = 'asc' }
  renderTable(document.querySelector<HTMLElement>('#grid-table')!)
  renderCount()
  syncSortControls()
  // The re-render rebuilt every header button, which would otherwise drop
  // focus on the floor: put it back on the clicked column's new button so
  // pressing Enter again reverses the same column without a Tab. preventScroll
  // keeps the scroll position renderTable just restored.
  document.querySelector<HTMLButtonElement>(`#grid-table button[data-sort-key="${key}"]`)?.focus({ preventScroll: true })
}

// The direction control's label must say what reversing DOES. On the order-
// added sort the generic "ascending/descending" hides the meaning, so it names
// the direction in sheet terms instead.
// The arrow is drawn, not typed (the style kit, Icons).
function dirControlLabel(): { text: string; aria: string } {
  if (sortBy === 'added') {
    return sortDir === 'asc'
      ? { text: 'Sheet order', aria: 'Sort direction: oldest first, the original sheet order. Activate to reverse to newest added first.' }
      : { text: 'Newest added first', aria: 'Sort direction: newest added first. Activate to reverse to the original sheet order.' }
  }
  return sortDir === 'desc'
    ? { text: 'Descending', aria: 'Sort direction: descending. Activate to reverse.' }
    : { text: 'Ascending', aria: 'Sort direction: ascending. Activate to reverse.' }
}
function drawDir(button: HTMLElement): void {
  const label = dirControlLabel()
  clear(button)
  button.append(lineDrawing(sortDir === 'desc' ? 'arrowDown' : 'arrowUp', 16), el('span', {}, label.text))
  button.setAttribute('aria-label', label.aria)
}

function syncSortControls(): void {
  const sel = document.querySelector<HTMLSelectElement>('select[aria-label="Sort customers"]')
  if (sel && SORT_OPTIONS.some(([v]) => v === sortBy)) sel.value = sortBy
  const dir = document.querySelector<HTMLButtonElement>('button[data-sort-dir]')
  if (dir) drawDir(dir)
}
function renderCards(): void {
  const container = document.querySelector<HTMLElement>('#grid-cards')
  if (!container) return
  clear(container)
  for (const c of rowsToShow()) {
    if (viewFilter === 'recanvass' || viewFilter === 'taken_off') {
      const card=el('article',{class:'customer-card customer-card-visit customer-list-card'},
        el('a',{href:`#/customer/${c.id}`,class:'name-link'},c.name),el('p',{class:'muted'},addressLine(c.address, c.postcode, ' · ')),listSummary(c),
        el('a',{href:`#/customer/${c.id}`,class:'btn-primary work-list-review'},'Review customer'))
      if (c.future_visit_count) card.append(el('p',{class:'customer-list-booking-warning'},`${c.future_visit_count} future booking${c.future_visit_count===1?'':'s'} still in the diary. Review before anyone goes.`))
      card.append(el('p',{class:'work-visit-help muted'},viewFilter==='recanvass'?'A handpicked customer worth another try. Review dates are reminders; contact is your choice.':'Out of active planning and routine contact. History and any money owed are kept.'))
      container.append(card);continue
    }
    const visiting = viewFilter === 'visit'
    const card = el('article', { class: `customer-card${visiting ? ' customer-card-visit' : ''}` })
    const regular = visiting ? el('div', { class: 'work-round-details-body' }) : card
    const date = c.next_booked || dueDate(c)
    // Design A: the style kit's job card, name with the regular price beside it (labelled, so it never reads as the
    // booked visit's price), the street, the next visit or due day, its tags, then one row of buttons.
    const name = el('a', { href: `#/customer/${c.id}`, class: 'name-link' }, c.name)
    // Design B (Liam, 26 Oct 2026, "B but with Joels name underlined like A"): the whole card opens their page, as
    // Messages' rows do; a lemon arrow by the price says so and the name is underlined. Its buttons keep their own jobs.
    card.append(visiting ? name : el('div', { class: 'cc-top' }, name, el('span', { class: 'cc-top-end' },
      el('span', { class: 'cc-price' }, el('strong', {}, money(c.reg_price_pence)), el('small', {}, 'regular')),
      el('span', { class: 'cc-open', 'aria-hidden': 'true' }, lineDrawing('chevronRight', 16)))),
      el('p', { class: 'muted cc-addr' }, addressLine(c.address, c.postcode, ' · ')),
      ...(visiting ? [visitSummary(c)] : []))
    if (visiting) {
      const cycleDate = dueDate(c)
      regular.append(el('p', {}, `Regular round: ${cycleLabel(c.cycle_days, c.cycle_text)} · ${money(c.reg_price_pence)}`),
        el('p', {}, `Regular cycle due: ${cycleDate ? shortDate(cycleDate) : 'Not set'}${cycleDate && dueIsGuessed(c) ? ' · Guessed · set cycle' : ''}`))
      if (c.next_booked) regular.append(el('p', {}, `Next booked visit: ${shortDate(c.next_booked)}`))
    } else {
      const cycle = c.cycle_days || c.cycle_text ? ` · ${cycleLabel(c.cycle_days, c.cycle_text)}` : ''
      regular.append(el('p', { class: 'cc-when' }, `${c.next_booked ? 'Next visit' : 'Due'} ${date ? dayLabel(date) : 'not set'}${cycle}`))
    }
    const tags = el('div', { class: 'cc-tags' })
    if (!c.next_booked && c.status === 'active') tags.append(el('span', { class: 'badge badge-unbooked', title: 'No next visit booked. This is not about payment.' }, 'Not rebooked'))
    if (c.next_booked) tags.append(nextVisitBadge(c))
    if (tags.firstChild) regular.append(tags)
    // Latest visit payment on the card too, labelled the same way as the sheet.
    if (c.last_clean) {
      const payLine = el('p', { class: 'muted cc-last' }, `Last clean ${shortDate(c.last_clean)} · latest visit `)
      payLine.append(lastPaymentBadge(c))
      regular.append(payLine)
    }
    if (c.overdue_bookings && !rebooked(c)) regular.append(el('p', { class: 'due-overdue' }, `${c.overdue_bookings} past booking${c.overdue_bookings === 1 ? '' : 's'} to review`))
    const book = el('button', { type: 'button', class: 'btn-primary' }, c.next_booked ? 'Edit visit' : 'Book visit')
    book.addEventListener('click', () => openBooking({ customerId: c.id, cleanId: c.next_booked_id ?? undefined }))
    // In the details view the card says WHY the customer is listed, so the
    // fix happens right here at the edit point rather than after a detour.
    if (viewFilter === 'details') {
      const reasons = detailsReasons(c)
      if (reasons.length) card.append(el('p', { class: 'muted' }, `Needs: ${reasons.join(' · ')}`))
    }
    // The extras the owner ticked, on the card too: on a phone the sheet is
    // hidden entirely, so a column he cannot see is a column he did not get.
    // A field he asked for with nothing in it still shows, labelled empty —
    // "no tier set" is the answer to "what tier is she", not a missing line.
    // The phone already shows the full address above; do not repeat it.
    const extras = chosenExtras().filter(col => col.field !== 'address')
    if (extras.length) {
      const text = extras.map(col => `${col.label}: ${displayValue(c, col) || 'not set'}`).join(' · ')
      regular.append(el('p', { class: 'muted card-extras' }, text))
    }
    const contact = el('button', { type: 'button', 'aria-label': `Message ${c.name}` }, 'Message')
    contact.addEventListener('click', () => openFollowupDialog('customers', c))
    const summary = followupSummary(c, todayISO(), 'customer')
    if (summary) regular.append(el('p', { class: 'muted' }, summary))
    if (visiting) {
      const actions = el('div', { class: 'work-visit-actions' }, book, visitDetailsButton(c))
      card.append(actions, el('p', { class: 'work-visit-help muted' }, 'The review date is when to check back. Book the visit on the date you arrange.'),
        el('details', { class: 'work-round-details' }, el('summary', {}, 'Regular round and history'), regular))
    } else {
      card.append(el('div', { class: 'cc-acts' }, book, contact))
      card.classList.add('cc-opens')
      card.addEventListener('click', (e) => {
        // A button, link or box on the card does its own job; text being picked out is not a tap.
        if ((e.target as Element | null)?.closest?.('a, button, input, select, textarea, label, summary')) return
        if (window.getSelection?.()?.toString()) return
        window.location.hash = `#/customer/${c.id}`
      })
    }
    container.append(card)
  }
}
function renderTable(container: HTMLElement): void {
  const position = { top: container.scrollTop, left: container.scrollLeft }
  clear(container)
  const table = el('table', { class: 'sheet', 'aria-label': 'Customer work sheet' })
  const thead = el('thead', {})
  const headRow = el('tr', {})
  for (const col of columns()) {
    const key = sortKeyForColumn(String(col.field))
    // Sortable headers strip the th's own padding (class th-sort-cell) so the
    // button below can fill the whole cell — dead space in a header that
    // ignores taps reads as a broken control.
    const th = el('th', { scope: 'col', ...(key ? { class: 'th-sort-cell' } : {}), ...(col.width ? { style: `min-width:${col.width}` } : {}) })
    if (!key) {
      // The Booking column is an action, not a value — a sort button there
      // would pretend to order something it cannot order.
      th.textContent = col.label
      headRow.append(th)
      continue
    }
    const active = sortBy === key
    th.setAttribute('aria-sort', active ? (sortDir === 'desc' ? 'descending' : 'ascending') : 'none')
    // A real button (not a click-to-sort th): the header must be reachable and
    // operable from the keyboard like any other control on this screen.
    const btn = el(
      'button',
      {
        type: 'button',
        class: `th-sort${active ? ' th-sort-active' : ''}`,
        'data-sort-key': key,
        'aria-label': active
          ? `Sort by ${col.label}, currently ${sortDir === 'desc' ? 'descending' : 'ascending'}`
          : `Sort by ${col.label}`,
      },
      col.label,
      el('span', { class: 'sort-arrow', 'aria-hidden': 'true' }, active ? (sortDir === 'desc' ? '↓' : '↑') : ''),
    )
    btn.addEventListener('click', () => applySort(key))
    th.append(btn)
    headRow.append(th)
  }
  thead.append(headRow); table.append(thead)
  const tbody = el('tbody', {})
  const rows = rowsToShow()
  for (const c of rows) {
    const tr = el('tr', { 'data-customer-id': String(c.id) })
    if (c.status === 'lapsed') tr.classList.add('row-lapsed')
    for (const col of columns()) tr.append(makeCell(c.id, col))
    tbody.append(tr)
  }
  table.append(tbody); container.append(table)
  const first = tbody.querySelector('td')
  if (first) first.tabIndex = 0
  container.scrollTop = position.top; container.scrollLeft = position.left
  renderCards()
  const empty = document.querySelector<HTMLElement>('#grid-empty')
  // A round with no customers at all has nothing to clear: say how to start.
  if (empty) {
    empty.hidden = rows.length > 0
    empty.textContent = customers.size === 0
      ? 'No customers yet. Add your first one above.'
      : ['recanvass','taken_off'].includes(viewFilter) && !listFieldsKnown() ? 'Reconnect to refresh the round lists.'
      : viewFilter === 'visit' && !visitFieldsKnown()
        ? 'Reconnect to refresh Needs a visit.'
        : 'No customers match. Clear filters to see more customers.'
  }
}

function addCustomerRow(container: HTMLElement): void {
  const form = el('form', { class: 'add-form add-form-work' })
  const name = el('input', { placeholder: 'name (required)', required: 'required', 'aria-label': 'New customer name' })
  const address = el('input', { placeholder: 'address', 'aria-label': 'New customer address' })
  const postcode = el('input', { placeholder: postcodeWords('postcode'), 'aria-label': postcodeWords('New customer postcode') })
  const price = el('input', { placeholder: `price ${currencySymbol()}`, 'aria-label': 'New customer regular price', inputmode: 'decimal' })
  const otherJobs=otherJobsChoice()
  name.addEventListener('input',()=>otherJobs.clear())
  const submit = el('button', { type: 'submit', class: 'btn-primary' }, 'Add customer')
  form.append(...[[name, 'Name'], [address, 'Address'], [postcode, postcodeWords('Postcode')], [price, `Regular price ${currencySymbol()}`]].map(([input, label]) => el('label', { class: 'booking-field' }, String(label), input as HTMLInputElement)), otherJobs.node, submit)
  form.addEventListener('submit', (e) => {
    e.preventDefault()
    let pence: number | null
    try {
      pence = parseMoney(price.value)
    } catch (err) {
      window.alert((err as Error).message)
      return
    }
    submit.setAttribute('disabled', 'disabled')
    void mutate({
      label: `Add customer: ${name.value}`,
      method: 'POST',
      path: '/api/customers',
      body: {
        name: name.value.trim(),
        address: address.value.trim() || null,
        postcode: postcode.value.trim() || null,
        reg_price_pence: pence, other_jobs:otherJobs.value(),
      },
      onAck: async () => {
        if (roundConfirmation('Customer added') !== 'Customer added') toast('Customer added')
        await load()
      },
    }).then((ack) => {
      // Re-enable ONLY on terminal rejection (codex CRITICAL-1 / kimi
      // LB-3): while the mutation is queued offline the form stays parked —
      // a re-tap would mint a NEW idempotency key and double-create the
      // customer when signal returns.
      if (!ack.ok && ack.status >= 400 && ack.status < 500) {
        submit.removeAttribute('disabled')
        window.alert(ack.error ?? 'rejected')
      } else if (!ack.ok) {
        submit.textContent = 'Waiting for signal'
      }
    })
  })
  container.append(form)
}

// A read on coming back to Work (come-back.ts) is quiet: if it fails, Work stays as it is, with no error panel.
let quietNext = false

async function load(): Promise<void> {
  const gen = routeGen // codex HIGH-3: bail before render if the user navigated away mid-load
  const sequence = ++requestSequence
  const quiet = quietNext
  quietNext = false
  const root = document.querySelector<HTMLElement>('#screen')!
  try {
    const data = await api<{ customers: Customer[] }>('/api/customers', 'customers')
    if (gen !== routeGen || sequence !== requestSequence) return
    if (editing) { refreshPending = true; return }
    // A quiet read that lands after Add customer was opened leaves the form and what was typed alone (Codex, 15 Oct).
    if (quiet && gridBusy()) return
    render(data.customers, root)
  } catch (err) {
    if (gen !== routeGen || sequence !== requestSequence || quiet) return
    const cached = readCache<{ customers: Customer[] }>('customers')
    if (cached) {
      render(cached.data.customers, root)
      loadError(root, (err as Error).message, () => void load(), cached.at)
    } else {
      clear(root)
      loadError(root, (err as Error).message, () => void load())
    }
  }
}

// The More filters fold closes on a tap anywhere else, and on Escape, the
// same as the Columns fold. Bound once: render() builds a fresh fold each time.
let moreDismissBound = false
function bindMoreDismiss(): void {
  if (moreDismissBound) return
  moreDismissBound = true
  document.addEventListener('click', (e) => {
    for (const more of document.querySelectorAll<HTMLDetailsElement>('details.view-more, details.grid-tools')) if (more.open && !more.contains(e.target as Node)) more.open = false
  })
  document.addEventListener('keydown', (e) => {
    if (e.key !== 'Escape') return
    for (const more of document.querySelectorAll<HTMLDetailsElement>('details.view-more, details.grid-tools')) if (more.open) more.open = false
  })
}

function render(list: Customer[], root: HTMLElement): void {
  restoreColumns()
  const prev = document.querySelector<HTMLElement>('#grid-table')
  if (prev) savedScroll = { left: prev.scrollLeft, top: prev.scrollTop }
  customers.clear()
  for (const c of list) customers.set(c.id, c)
  clear(root)
  const heading = el('div', { class: 'page-heading plan-heading' }, el('div', {}, el('h2', {}, 'Plan the round'), el('p', { class: 'muted' }, 'Find who is due, then book the work.')))
  const headActs = el('div', { class: 'page-heading-acts' })
  heading.append(headActs)
  root.append(heading)
  const bar = el('div', { class: 'screen-bar work-bar' })
  const search = el('input', { type: 'search', class: 'search-box', placeholder: postcodeWords('Name, address, postcode or phone'), 'aria-label': 'Search customers' })
  search.value = filterText
  const refreshList = (): void => { renderTable(document.querySelector<HTMLElement>('#grid-table')!); renderCount() }
  search.addEventListener('input', () => { filterText = search.value; refreshList() })
  const select = (label: string, entries: string[][], value: string, update: (v: string) => void): HTMLSelectElement => {
    const s = el('select', { 'aria-label': label })
    for (const [v, text] of entries) s.append(el('option', { value: v }, text))
    s.value = value; s.addEventListener('change', () => { update(s.value); refreshList() }); return s
  }
  const status = select('Customer status', [['active', 'Active customers'], ['all', 'All customers'], ['paused', 'Paused'], ['stopped', 'Taken off'], ['lapsed', 'Lapsed / no reply']], statusFilter, v => statusFilter = v)
  // Rename the chosen area beside the box (area-rename.ts, Liam's pick A, 27 Oct 2026); the box stays the filter.
  const areaRename = el('span', { class: 'grid-area-rename' })
  const syncRename = (): void => { while (areaRename.firstChild) areaRename.firstChild.remove(); if (areaFilter && areaFilter !== 'Not set') areaRename.append(areaRenameLink(areaFilter)) }
  const area = select('Area', [['', 'All areas'], ...[...new Set(list.map(areaOf))].sort().map(v => [v, v])], areaFilter, v => { areaFilter = v; syncRename() })
  syncRename()
  // The dropdown is the same control it always was, just complete: picking a
  // field keeps the current direction except order-added, which restores the
  // original sheet order. The direction button and headers own
  // the toggle, and re-renders — including coming back from a customer —
  // always set `value` from sortBy, so the visible selection matches the
  // actual order. The select() helper already calls refreshList(), so the
  // callback only updates state.
  const sort = select('Sort customers', SORT_OPTIONS.map(([v, label]): [string, string] => [v, postcodeWords(label)]), sortBy, v => {
    sortBy = v
    if (v === 'added') sortDir = 'asc' // choosing this always restores original order
    syncSortControls()
  })
  // Direction control beside the dropdown: on a phone the planning sheet is
  // cards with no headers to click, so without this there is no way at all to
  // reverse the order.
  const dirBtn = el('button', { type: 'button', class: 'sort-dir', 'data-sort-dir': 'true' })
  drawDir(dirBtn)
  dirBtn.addEventListener('click', () => { sortDir = sortDir === 'asc' ? 'desc' : 'asc'; refreshList(); syncSortControls() })
  const reset = el('button', { type: 'button', 'data-clear-filters': '' }, 'Clear filters')
  // Reset re-renders from the CURRENT map, not the `list` this render() call
  // was handed: inline edits ack into `customers` between renders, and a
  // reset that re-fed the original fetch list would throw those acknowledged
  // edits off the screen (the same source the view toggle already uses).
  reset.addEventListener('click', () => { filterText = ''; areaFilter = ''; statusFilter = 'all'; viewFilter = 'all'; render([...customers.values()], root) })
  const toggle = el('button', { type: 'button', 'aria-pressed': String(fullSheet) }, fullSheet ? 'Planning view' : 'Full sheet')
  toggle.addEventListener('click', () => { fullSheet = !fullSheet; render([...customers.values()], root) })
  const addBtn = el('button', { type: 'button', class: 'btn-primary' }, '+ Add customer')
  const addWrap = el('div', { class: 'add-wrap' })
  addBtn.addEventListener('click', () => {
    // A cancel is never the lemon main button (Liam's look answer, 8 Oct 2026): the form's own "Add customer" is.
    if (addWrap.firstChild) { clear(addWrap); addBtn.textContent = '+ Add customer'; addBtn.classList.add('btn-primary') }
    else { addCustomerRow(addWrap); addBtn.textContent = 'Cancel new customer'; addBtn.classList.remove('btn-primary'); addWrap.querySelector('input')?.focus() }
  })
  // Extra columns: tick one and it joins the sheet AND the phone cards, and
  // its header sorts like any other. Kept as a fold rather than a dialog so
  // the list can stay open while several are ticked on the trot.
  const columnPicker = (): HTMLElement => {
    bindPickerDismiss()
    const on = chosenExtras().length
    const box = el('details', { class: 'col-picker' })
    box.append(el('summary', {}, on ? `Columns · ${on}` : 'Columns'))
    const list = el('div', { class: 'col-picker-list' })
    for (const col of EXTRA_COLS) {
      // A field the current view already shows is not an "extra" here — it is
      // offered again only when the view it belongs to is not on screen.
      if (isStandard(col.field)) continue
      const id = `extra-${col.field}`
      const tick = el('input', { type: 'checkbox', id })
      tick.checked = extraOn.has(col.field)
      tick.addEventListener('change', () => {
        if (tick.checked) extraOn.add(col.field)
        else {
          extraOn.delete(col.field)
          // Sorting by a column that is no longer on screen would leave the
          // sheet in an order nothing explains: fall back to the default.
          if (sortBy === sortKeyForColumn(String(col.field))) { sortBy = 'added'; sortDir = 'asc'; syncSortControls() }
        }
        writeWorkColumns(columnsBusiness, extraOn, extraFields)
        refreshList()
        const summary = box.querySelector('summary')
        const count = chosenExtras().length
        if (summary) summary.textContent = count ? `Columns · ${count}` : 'Columns'
      })
      list.append(el('label', { class: 'col-picker-row', for: id }, tick, col.label))
    }
    box.append(list)
    return box
  }
  // Many customers at once, from a spreadsheet or another app (#/import),
  // and the whole list out again as a spreadsheet the owner keeps.
  // Their own row under the heading, left-aligned and matching each other
  // (Liam 2026-09-27: the lone blue "Import a list" link pushed right read as
  // rubbish next to the boxed download).
  const importLink = el('a', { href: '#/import', class: 'grid-list-btn' }, 'Import a list')
  const downloadBtn = el('button', { type: 'button', class: 'grid-list-btn' }, 'Download your customers')
  downloadBtn.addEventListener('click', () => void downloadCustomers(downloadBtn))
  // Every visit with its price and payment, beside it and matching it (27 Oct 2026).
  const visitsBtn = el('button', { type: 'button', class: 'grid-list-btn' }, 'Download your visits and payments')
  visitsBtn.addEventListener('click', () => void downloadVisits(visitsBtn))
  // Design A: Full sheet, Import, Download and Columns behind one ⋯ beside the filters. Full sheet's way back, Planning
  // view, stays beside Add customer, never hidden in the menu.
  const tools = el('details', { class: 'grid-tools' })
  tools.append(el('summary', { 'aria-label': 'Full sheet, import, download and columns' }, lineDrawing('dots', 20), el('span', { class: 'grid-tools-words' }, 'Sheet, import, columns')))
  tools.append(el('div', { class: 'grid-tools-list' }, ...(fullSheet ? [] : [toggle]), importLink, downloadBtn, visitsBtn, columnPicker()))
  headActs.append(...(fullSheet ? [toggle] : []), addBtn)
  bar.append(search, status, area, areaRename, sort, dirBtn, tools, reset)
  const views = el('div', { class: 'view-tabs work-view-tabs list-tiles', 'aria-label': 'Booking filters' })
  const primaryViews = el('div', { class: 'work-primary-views' })
  const otherViews = el('div', { class: 'work-other-views' })
  views.append(primaryViews, otherViews)
  const more = el('details', { class: 'view-more' })
  const moreSummary = el('summary', { 'aria-label': 'More filters' }, el('span', { class: 'list-tile-ring' }, lineDrawing('dots', 19)), el('span', { class: 'view-more-words' }, 'More lists'))
  const moreList = el('div', { class: 'view-more-list' })
  more.append(moreSummary, moreList)
  const tiles = new Map<string, HTMLElement>()
  for (const [key, label] of VIEWS) {
    const drawing = TILE_DRAWINGS[key]
    const btn = drawing
      ? el('button', { type: 'button', class: 'list-tile', 'data-view': key, 'aria-pressed': String(key === viewFilter) },
        el('span', { class: 'list-tile-ring' }, lineDrawing(drawing, 19)), el('span', { class: 'list-tile-count' }), el('span', { class: 'list-tile-label' }, label))
      : el('button', { type: 'button', 'data-view': key, 'aria-pressed': String(key === viewFilter) }, label)
    // Refresh the list and counts only: a rebuild would wipe a half-typed
    // new customer and lose the focus (Astra review 2026-09-25).
    btn.addEventListener('click', () => {
      viewFilter = key
      if (key === 'recanvass' || key === 'taken_off') {statusFilter='all';status.value='all'}
      const fromMore = more.open
      more.open = false
      refreshList()
      ;(fromMore ? moreSummary : btn).focus()
    })
    if (drawing) tiles.set(key, btn)
    else moreList.append(btn)
  }
  // The tiles in the drawing's order: All, Due now, To confirm, Going cold, Needs a visit, Missing a price or cycle.
  for (const key of Object.keys(TILE_DRAWINGS)) { const tile = tiles.get(key); if (tile) primaryViews.append(tile) }
  otherViews.append(more)
  bindMoreDismiss()
  const tableWrap = el('div', { id: 'grid-table', class: `sheet-wrap ${fullSheet ? 'full-sheet' : 'planning-sheet'}` })
  tableWrap.addEventListener('scroll', () => { savedScroll = { left: tableWrap.scrollLeft, top: tableWrap.scrollTop } })
  const cards = el('div', { id: 'grid-cards', class: `customer-cards${fullSheet ? ' hide-cards' : ''}` })
  // Colour key, compact by default: the payment colours say what the LATEST
  // completed visit was, and the blue cue is not a payment state at all —
  // without this line the two read as one system.
  const key = el('details', { class: 'colour-key' }, el('summary', {}, 'Colour key'))
  const keyRow = (badgeClass: string, badgeText: string, meaning: string): HTMLElement =>
    el('p', {}, el('span', { class: badgeClass }, badgeText), `: ${meaning}`)
  // The badges' own words (Owed's, visit-words.ts paymentWords), so the key names what the list shows.
  key.append(
    keyRow('badge badge-paid', 'Paid', 'the latest completed visit was paid by bank'),
    keyRow('badge badge-cash', 'Paid cash', 'the latest completed visit was paid in cash'),
    keyRow('badge badge-unpaid', 'Not paid', 'the latest completed visit is not paid yet'),
    keyRow('badge badge-chased', 'Chased · not paid', 'not paid, and they have been chased'),
    keyRow('badge badge-unbooked', 'Not rebooked', 'no next visit booked yet. Current state, not a payment.'),
  )
  // The colour key and keyboard help belong with the spreadsheet (Full
  // sheet); the planning list reads without them.
  root.append(bar, addWrap, views, el('p', { class: 'grid-help muted', id: 'grid-details-note' }), el('p', { class: 'bar-note', id: 'grid-count', role: 'status' }),
    ...(fullSheet ? [key, el('p', { class: 'grid-help muted' }, 'Select a cell to edit. Keyboard: arrow keys to move, Enter to edit, Escape to cancel.')] : []),
    cards, tableWrap, el('p', { id: 'grid-empty', class: 'empty-note' }))
  const keep = { ...savedScroll }
  renderTable(tableWrap); tableWrap.scrollLeft = keep.left; tableWrap.scrollTop = keep.top
  renderCount()
  // #/grid?add=1 (the diary's welcome for a new round) opens the add form.
  if (openAddForm) { openAddForm = false; if (!addWrap.firstChild) addBtn.click() }
}

function listFieldsKnown(): boolean { return [...customers.values()].every(c => 'customer_list' in c) }
function visitFieldsKnown(): boolean { return [...customers.values()].every(c => 'messages_shelf' in c) }
function renderCount(): void {
  const riskKnown = confirmFieldsKnown([...customers.values()])
  const needsAggregates = (view: string): boolean => ['slipping', 'cold', 'confirm'].includes(view)
  const node = document.querySelector<HTMLElement>('#grid-count')
  if (node) node.textContent = `${rowsToShow().length} of ${customers.size} customers`
  // The details view's coverage, spelled out where the user edits customers:
  // a customer who said "ad-hoc" / "on request" has ANSWERED the cycle
  // question, so only the price (or a blank / TBC cycle) keeps them listed.
  const note = document.querySelector<HTMLElement>('#grid-details-note')
  if (note) {
    note.textContent = viewFilter === 'details'
      ? 'Missing a price or cycle: no regular price, or no cycle agreed. Ad-hoc, on request and one off count as agreed. The cards list each customer\'s reasons.'
      : viewFilter === 'visit' ? `Customers set aside for an in-person visit. Review dates are reminders.${statusFilter !== 'all' ? ' Choose All customers to include paused or lapsed customers.' : ''}` : ''
  }
  if (!riskKnown && note) note.textContent = 'Slipping, going cold and confirmation counts need an updated customer list. They are unknown while using this older saved copy.'
  if (!riskKnown && needsAggregates(viewFilter) && node) node.textContent = 'Count unavailable. Reconnect to refresh the customer list.'
  if (viewFilter === 'visit' && !visitFieldsKnown()) {
    if (note) note.textContent = 'Needs a visit needs an updated customer list. Reconnect to refresh it.'
    if (node) node.textContent = 'Count unavailable. Reconnect to refresh the customer list.'
  }
  if (['recanvass','taken_off'].includes(viewFilter) && !listFieldsKnown()) {if(note)note.textContent='Reconnect to refresh the round lists.';if(node)node.textContent='Count unavailable. Reconnect to refresh the customer list.'}
  else if (note && viewFilter==='recanvass') note.textContent='Old customers you have chosen to try again. Review dates are reminders.'
  else if (note && viewFilter==='taken_off') note.textContent='Taken off the round. History and money are kept; review any remaining bookings.'
  for (const [key, label] of VIEWS) {
    const btn = document.querySelector<HTMLButtonElement>(`button[data-view="${key}"]`)
    if (btn) {
      const unknown = ['recanvass','taken_off'].includes(key) && !listFieldsKnown() || !riskKnown && needsAggregates(key) || key === 'visit' && !visitFieldsKnown()
      btn.disabled = unknown
      const count = unknown ? '—' : String([...customers.values()].filter(c => baseMatches(c,key,true) && viewMatches(c, key)).length)
      const tileCount = btn.querySelector('.list-tile-count')
      if (tileCount) { tileCount.textContent = count; btn.setAttribute('aria-label', `${label}, ${unknown ? 'count unavailable' : count}`) }
      else btn.textContent = `${label} · ${count}`
      btn.setAttribute('aria-pressed', String(key === viewFilter))
    }
  }
  // Clear filters shows only when something is filtered, after any change.
  const clearBtn = document.querySelector<HTMLButtonElement>('[data-clear-filters]')
  if (clearBtn) clearBtn.hidden = !(filterText || areaFilter || (statusFilter !== 'active' && statusFilter !== 'all') || viewFilter !== 'all')
  const moreSummary = document.querySelector<HTMLElement>('.view-more > summary')
  if (moreSummary) {
    const hidden = MAIN_VIEWS.has(viewFilter) ? null : VIEWS.find(([k]) => k === viewFilter)
    const words = moreSummary.querySelector('.view-more-words')
    if (words) words.textContent = hidden ? `More: ${hidden[1]}` : 'More lists'
    moreSummary.classList.toggle('view-more-on', !!hidden)
  }
}

// Route query support: the diary's round summary links here as
// #/grid?view=slipping&status=all (main.ts's startsWith('#/grid') routing
// already carries any query through untouched). Only known keys apply; a
// plain #/grid keeps whatever filters this screen already had.
const VIEW_KEYS: Set<string> = new Set(VIEWS.map(([key]) => key))
const STATUS_KEYS = new Set(['active', 'all', 'paused', 'stopped', 'lapsed'])

let openAddForm = false

function applyRouteQuery(): void {
  openAddForm = false
  const hash = window.location.hash
  const q = hash.indexOf('?')
  if (q === -1) return
  const params = new URLSearchParams(hash.slice(q + 1))
  if (params.get('add') === '1') {
    openAddForm = true
    // Taken out of the address at once, so pull-to-refresh or Back to this
    // page does not open the form again (review 2026-09-25).
    params.delete('add')
    const rest = params.toString()
    history.replaceState(history.state, '', `${hash.slice(0, q)}${rest ? `?${rest}` : ''}`)
  }
  const view = params.get('view')
  if (view && VIEW_KEYS.has(view)) {
    viewFilter = view
    // Summary links describe the whole round, regardless of previous search.
    filterText = ''
    areaFilter = ''
  }
  if (view === 'recanvass' || view === 'taken_off') statusFilter='all'
  const status = params.get('status')
  if (status && STATUS_KEYS.has(status)) statusFilter = status
}

export function mountGrid(): void {
  applyRouteQuery()
  void load()
}

export function gridOnDataChanged(): void {
  if (editing) refreshPending = true
  else void load()
}

// Coming back to Work (main.ts, come-back.ts): read again, quietly, unless a cell is being edited.
export function gridBusy(): boolean {
  // The Add customer form open counts too: a redraw would close it with what was typed.
  return editing || !!document.querySelector('.add-form-work')
}
export function gridComeBack(): void {
  if (gridBusy()) return
  quietNext = true
  void load()
}

// "Download your customers": every customer on the round (active, paused and
// stopped) as a spreadsheet, fresh from the server so nothing cached is missed,
// with what each owes now from Owed's own read.
async function downloadCustomers(btn: HTMLButtonElement): Promise<void> {
  if (getMe()?.role !== 'owner') return
  btn.disabled = true
  try {
    const [data, owed] = await Promise.all([api<{ customers: Customer[] }>('/api/customers', 'customers'), api<OwedRead>('/api/owed')])
    const owing = owedByCustomer(owed)
    saveCsv(customersCsv(data.customers, postcodeWords, { owedPence: (c) => owing.get(c.id) ?? 0, dueOn: dueDate, symbol: currencySymbol() }), `roundly-customers-${todayISO()}.csv`)
    toast(`${data.customers.length} customers downloaded`)
  } catch {
    toast("Couldn't download your customers. Check your signal and try again.")
  } finally {
    btn.disabled = false
  }
}

// "Download your visits and payments": every visit the round holds, oldest first (GET /api/visits-download, owner only).
async function downloadVisits(btn: HTMLButtonElement): Promise<void> {
  if (getMe()?.role !== 'owner') return
  btn.disabled = true
  try {
    const data = await api<{ visits: VisitRow[] }>('/api/visits-download')
    saveCsv(visitsCsv(data.visits, currencySymbol(), postcodeWords), `roundly-visits-${todayISO()}.csv`)
    toast(`${data.visits.length} visits downloaded`)
  } catch {
    toast("Couldn't download your visits. Check your signal and try again.")
  } finally {
    btn.disabled = false
  }
}

function saveCsv(csv: string, name: string): void {
  const url = URL.createObjectURL(new Blob([csv], { type: 'text/csv;charset=utf-8' }))
  const a = el('a', { href: url, download: name })
  document.body.append(a)
  a.click()
  a.remove()
  // Revoked later: revoking straight away races the download in Safari.
  window.setTimeout(() => URL.revokeObjectURL(url), 10_000)
}
